/**
 * ConditionHelper - Helper pour évaluer les conditions
 * Identique à Flutter ConditionHelper
 */

import {GameState} from '../models/GameState';
import {PlayerStep} from '../models/PlayerStep';
import {PlayerPath} from '../models/PlayerPath';
import {PlayerAction} from '../models/PlayerAction';
import {PlayerCondition} from '../models/PlayerCondition';
import GameRegistry from '../systems/GameRegistry';

class ConditionHelper {
  /**
   * Vérifie si les conditions sont remplies
   * Logique: OR entre les groupes (groupNumber), AND dans chaque groupe
   */
  checkConditions(
    gameState: GameState,
    currentStep: PlayerStep,
    conditions: PlayerCondition[],
  ): boolean {
    if (conditions.length === 0) {
      console.log(`[ConditionHelper.checkConditions] No conditions, returning true`);
      return true;
    }

    console.log(`[ConditionHelper.checkConditions] Checking ${conditions.length} conditions`);
    console.log(`[ConditionHelper.checkConditions] GameState:`, {
      uniqueURI: gameState.uniqueURI,
      slot: gameState.slot,
      currentStepNumber: gameState.data.currentStepNumber,
      objects: gameState.data.objects?.map(o => ({id: o.attributeId, dropped: o.dropped, value: o.value})) || [],
      skills: gameState.data.skills?.map(s => ({id: s.attributeId, value: s.value})) || [],
      adventureExtraFields: gameState.data.adventureExtraFields?.map(f => ({type: f.type, currentValue: f.currentValue, initValue: f.initValue})) || [],
    });
    console.log(`[ConditionHelper.checkConditions] CurrentStep:`, {
      numero: currentStep.numero,
      pathsCount: currentStep.paths.length,
    });

    // Trier les conditions par groupe
    const conditionTree = new Map<number, PlayerCondition[]>();
    for (const condition of conditions) {
      const groupNumber = condition.groupNumber ?? 0;
      if (!conditionTree.has(groupNumber)) {
        conditionTree.set(groupNumber, []);
      }
      conditionTree.get(groupNumber)!.push(condition);
    }

    console.log(`[ConditionHelper.checkConditions] Condition groups: ${conditionTree.size}`);

    // Vérifier si au moins un groupe est valide (OR entre les groupes)
    for (const [groupNumber, group] of conditionTree.entries()) {
      console.log(`[ConditionHelper.checkConditions] Checking group ${groupNumber} with ${group.length} conditions`);
      let groupValid = true;
      // Toutes les conditions du groupe doivent être valides (AND dans le groupe)
      for (const condition of group) {
        const conditionResult = this._checkCondition(gameState, currentStep, condition);
        console.log(`[ConditionHelper.checkConditions] Group ${groupNumber}, condition ${condition.typeName}(${condition.attribute}, ${condition.value}): ${conditionResult}`);
        if (!conditionResult) {
          groupValid = false;
          break;
        }
      }
      if (groupValid) {
        console.log(`[ConditionHelper.checkConditions] Group ${groupNumber} is valid, returning true`);
        return true;
      } else {
        console.log(`[ConditionHelper.checkConditions] Group ${groupNumber} is invalid`);
      }
    }

    console.log(`[ConditionHelper.checkConditions] All groups invalid, returning false`);
    return false;
  }

  /**
   * Vérifie une condition individuelle
   */
  private _checkCondition(
    gameState: GameState,
    currentStep: PlayerStep,
    condition: PlayerCondition,
  ): boolean {
    const conditionType = GameRegistry.getConditionType(condition.typeName);

    // Vérifier si on utilise le type par défaut (TrueConditionType qui retourne toujours true)
    if (!conditionType || conditionType.typeName === 'TRUE') {
      console.error('################################################################################');
      console.error('###  ⚠️  ERREUR CRITIQUE : CONDITION TYPE NON TROUVÉE  ⚠️  ###');
      console.error(`###  Type de condition recherché: ${condition.typeName}`);
      console.error(`###  Type retourné par le registre: ${conditionType?.typeName ?? 'NULL'}`);
      console.error(`###  Step numero: ${currentStep.numero}`);
      console.error(`###  Condition attribute: ${condition.attribute}`);
      console.error(`###  Condition value: ${condition.value}`);
      console.error('###  ⚠️  Cette condition retournera FALSE !');
      console.error('###  SOLUTION: Ajouter l\'enregistrement dans BaseSystem');
      console.error('################################################################################');
      // Retourner false pour être sûr que la condition échoue
      return false;
    }

    // Créer une copie de currentStep avec les attributs de la condition si nécessaire
    const stepForEvaluation: PlayerStep = currentStep.gameState
      ? currentStep
      : {
          ...currentStep,
          gameState,
          attribute: condition.attribute,
          value: condition.value,
        };

    // Passer currentStep aux conditions qui en ont besoin (comme StepSucceedConditionType, etc.)
    const result = conditionType.evaluate(gameState, condition, stepForEvaluation);
    return result;
  }

  /**
   * Filtre les chemins disponibles en fonction des conditions
   * En mode cheat, retourne tous les paths (disponibles + cachés)
   */
  filterPaths(
    step: PlayerStep,
    gameState: GameState,
    cheat: boolean = false,
  ): PlayerPath[] {
    console.log(`[ConditionHelper] filterPaths CALLED with cheat parameter: ${cheat}, type: ${typeof cheat}`);
    // S'assurer que step a gameState pour que les conditions fonctionnent
    const stepWithGameState: PlayerStep = step.gameState
      ? step
      : {
          ...step,
          gameState,
        };

    // TOUJOURS filtrer les paths normalement pour identifier les paths cachés
    const filteredPaths = step.paths.filter((path) => {
      // Si le path n'a pas de conditions, il est toujours disponible
      if (path.conditions.length === 0) {
        console.log(`[ConditionHelper] Path "${path.description.substring(0, 50)}..." - No conditions, keeping path`);
        return true;
      }

      // Vérifier les conditions
      const result = this.checkConditions(gameState, stepWithGameState, path.conditions);

      // Log concis du résultat
      if (path.conditions.length > 0) {
        const condSummary = path.conditions
          .map((c) => `${c.typeName}(attr:${c.attribute},val:${c.value})`)
          .join(', ');
        console.log(
          `[ConditionHelper] Path "${path.description.substring(0, 50)}..." - Conditions: [${condSummary}] -> ${result}`,
        );
      }

      return result;
    });

    console.log(
      `[ConditionHelper] Total paths: ${step.paths.length}, Filtered paths: ${filteredPaths.length}`,
    );
    if (filteredPaths.length < step.paths.length) {
      const hiddenPaths = step.paths.filter(
        (p) => !filteredPaths.some((fp) => fp.description === p.description && fp.child === p.child),
      );
      console.log(
        `[ConditionHelper] Hidden paths: ${hiddenPaths.map((p) => `"${p.description}"`).join(', ')}`,
      );
    }

    // En mode cheat, retourner tous les paths (disponibles + cachés)
    // Sinon, retourner uniquement les paths filtrés (disponibles)
    console.log(`[ConditionHelper] filterPaths - cheat mode: ${cheat}, step.paths.length: ${step.paths.length}, filteredPaths.length: ${filteredPaths.length}`);
    
    if (cheat) {
      console.log(`[ConditionHelper] filterPaths - CHEAT MODE: returning ALL ${step.paths.length} paths`);
      return step.paths; // Retourner tous les paths en mode cheat
    }

    console.log(`[ConditionHelper] filterPaths - NORMAL MODE: returning ${filteredPaths.length} filtered paths`);
    return filteredPaths; // Retourner uniquement les paths disponibles en mode normal
  }

  /**
   * Filtre les actions disponibles en fonction des conditions
   */
  filterActions(step: PlayerStep, gameState: GameState): PlayerAction[] {
    return step.actions.filter((action) => {
      if (action.conditions.length === 0) return true;
      return this.checkConditions(gameState, step, action.conditions);
    });
  }

  /**
   * Évalue une condition simple (utilisé pour les événements, etc.)
   */
  evaluateCondition(gameState: GameState, condition: any, currentStep?: PlayerStep): boolean {
    const type = condition.type || condition.typeName;
    if (!type) {
      console.error('[ConditionHelper] evaluateCondition: missing type in condition');
      return false;
    }

    const conditionType = GameRegistry.getConditionType(type);
    if (!conditionType) {
      console.error(`[ConditionHelper] Unknown condition type in evaluateCondition: ${type}`);
      return false;
    }

    return conditionType.evaluate(gameState, condition, currentStep);
  }
}

export default new ConditionHelper();
